/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { clearActiveClinicId, getActiveClinicId } from '@/contexts/ClinicContext';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/';
const API_TIMEOUT = 30000; // 30 seconds

// Error types
export interface ApiError {
  message: string;
  status: number;
  code?: string;
  details?: any;
}

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
}

// Request interceptor to add auth token and selected clinic ID
const addAuthToken = (config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Add selected clinic ID header from clinic context (for multi-clinic users)
  const selectedClinicId = getActiveClinicId();
  if (selectedClinicId) {
    config.headers.set('X-Selected-Clinic-Id', selectedClinicId);
  }

  // Add selected org ID header if in org view (per Context Resolution Policy D)
  // This is informational only -- orgId comes from route params on org routes
  const selectedOrgId = localStorage.getItem('selectedOrgId');
  if (selectedOrgId) {
    config.headers.set('X-Selected-Org-Id', selectedOrgId);
  }
  
  return config;
};

// Response interceptor for error handling
const handleResponse = (response: AxiosResponse) => {
  return response;
};

const parseErrorData = async (
  data: any,
): Promise<{
  message?: string;
  error?: { message?: string; code?: string; details?: any };
  failure_message?: string;
  code?: string;
  details?: any;
} | null> => {
  if (!data) return null;
  if (typeof data === 'object' && !(data instanceof Blob)) {
    return data;
  }
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      return JSON.parse(text) as { message?: string; error?: { message?: string }; failure_message?: string; code?: string; details?: any };
    } catch {
      return null;
    }
  }
  return null;
};

const clearAuthState = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('selectedClinicId');
  localStorage.removeItem('selectedClinicTimestamp');
  localStorage.removeItem('currentRole');
  localStorage.removeItem('user');
  clearActiveClinicId();
};

const handleError = (error: any): Promise<never> => {
  // Pass through aborted/canceled requests - caller should ignore these
  if (error?.code === 'ERR_CANCELED' || (axios.isCancel && axios.isCancel(error))) {
    return Promise.reject(error);
  }
  if (error.response) {
    const requestUrl = error.config?.url || '';
    const isPublicTelehealth = /\/telehealth\/(join|waiting)\b/.test(requestUrl);
    const isPublicRoute = /^\/(onboard|practitioner-onboarding|invite|patient|intake|storefront\/link|book|p\/|chat|pcos|mdi-portal|patient-form|guided-order-amendment)/.test(window.location.pathname);

    const extractMessage = (data: any) =>
      data?.error?.message || data?.message || data?.failure_message || 'An error occurred';

    const processError = (data: any) => {
      if (data instanceof Blob) {
        return parseErrorData(data).then(parsed => ({
          message: extractMessage(parsed),
          code: parsed?.code || parsed?.error?.code,
          details: parsed?.details || parsed?.error?.details,
        }));
      }
      return Promise.resolve({
        message: extractMessage(data),
        code: data?.code || data?.error?.code,
        details: data?.details || data?.error?.details,
      });
    };

    const rejectWithError = (apiError: ApiError) => Promise.reject(apiError);

    if (error.response.status === 400 || error.response.status === 402) {
      return processError(error.response.data).then(({ message, code, details }) =>
        rejectWithError({
          message,
          status: error.response.status,
          code,
          details,
        })
      );
    }

    // Handle 401/403.
    //
    // 401 always means the session is no longer valid → clear auth and bounce
    // to /login. 403 is more nuanced: a generic role-mismatch ("you can't do
    // this specific action") should surface as an error to the caller, NOT
    // log the user out — otherwise routine permission errors look like a
    // session expiry. Only the codes that genuinely indicate the user has
    // lost access (CLINIC_INACTIVE, ACCOUNT_SUSPENDED, etc.) trigger logout.
    if (error.response.status === 401 || error.response.status === 403) {
      const requestUrl = error.config?.url || '';
      const isBackgroundCheck = requestUrl.includes('/billing/');
      const isLocalDevBypass = import.meta.env.DEV && import.meta.env.VITE_LOCAL_DEV_AUTH_BYPASS === 'true';

      return processError(error.response.data).then(({ message, code, details }) => {
        // Phase 5A: Trial expired — show modal instead of clearing auth + redirecting
        if (code === 'TRIAL_EXPIRED') {
          window.dispatchEvent(new CustomEvent('trial-expired'));
          return rejectWithError({
            message,
            status: error.response.status,
            code,
            details,
          });
        }

        const ACCESS_REVOKED_CODES = new Set([
          'CLINIC_INACTIVE',
          'ACCOUNT_SUSPENDED',
          'ACCOUNT_DISABLED',
          'TOKEN_EXPIRED',
          'INVALID_TOKEN',
          'SESSION_EXPIRED',
        ]);
        const shouldLogout
          = error.response.status === 401
          || (error.response.status === 403 && !!code && ACCESS_REVOKED_CODES.has(code));

        if (shouldLogout && !isPublicTelehealth && !isPublicRoute && !isBackgroundCheck && !isLocalDevBypass) {
          clearAuthState();

          if (code === 'CLINIC_INACTIVE') {
            window.alert('Your clinic has been deactivated. Please contact your administrator.');
          }

          // Only redirect if we're not already on the login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }

        return rejectWithError({
          message,
          status: error.response.status,
          code,
          details,
        });
      });
    }

    // Server responded with other error status
    return processError(error.response.data).then(({ message, code, details }) => {
      return rejectWithError({
        message,
        status: error.response.status,
        code,
        details,
      });
    });
  } else if (error.request) {
    // Request was made but no response received
    const apiError: ApiError = {
      message: 'No response from server. Please check your connection.',
      status: 0,
      code: 'NETWORK_ERROR',
    };
    return Promise.reject(apiError);
  } else {
    // Something else happened
    const apiError: ApiError = {
      message: error.message || 'An unexpected error occurred',
      status: 0,
      code: 'UNKNOWN_ERROR',
    };
    return Promise.reject(apiError);
  }
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor
apiClient.interceptors.request.use(addAuthToken, error => Promise.reject(error));

// Add response interceptor
apiClient.interceptors.response.use(handleResponse, handleError);

// Generic API methods that extract data from ApiResponse
export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.get(url, config).then(response => response.data),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.post(url, data, config).then(response => response.data),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.put(url, data, config).then(response => response.data),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.patch(url, data, config).then(response => response.data),

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.delete(url, config).then(response => response.data),
};

export default apiClient;
